import type { Column, Table } from "@tanstack/react-table"
import * as React from "react"
import { DataTableDateFilter } from "@/components/data-table/data-table-date-filter"
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter"
import { DataTableViewOptions } from "@/components/data-table/data-table-view-options"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/utils/shadcn-utils.js"
import ExportExcel from "@/utils/ExportExcel"
import { ExcelExportPropsType } from "@/types/data-table"
import { Close } from "@mui/icons-material"

interface DataTableToolbarProps<TData> extends React.ComponentProps<"div"> {
  table: Table<TData>
  excelExportData?: ExcelExportPropsType
  onDateSelected?: () => void
  selectedDate?: Date
  leftAddon?: React.ComponentType
  rightAddon?: React.ComponentType
  columnsAdjustmentHeight?: number
  reverseToolbarFilters?: boolean
  selectedDefaultDate?: Date | { from: Date; to: Date } | undefined
  hideColumnsAdjustment?: boolean
}

export function DataTableToolbar<TData>({
  table,
  children,
  className,
  onDateSelected,
  excelExportData,
  leftAddon,
  rightAddon,
  columnsAdjustmentHeight,
  selectedDate,
  reverseToolbarFilters = false,
  selectedDefaultDate,
  hideColumnsAdjustment = false,
  ...props
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0

  const columns = React.useMemo(() => {
    return table.getAllColumns().filter((column) => column.getCanFilter())
  }, [table.options.columns])

  const onReset = React.useCallback(() => {
    table.resetColumnFilters()
    if (onDateSelected) onDateSelected()
  }, [table])

  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn(
        "flex w-full items-start justify-between gap-2 p-1",
        className
      )}
      {...props}>
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {columns
          .sort(
            (a, b) =>
              (b.columnDef.meta?.priority ?? 0) -
              (a.columnDef.meta?.priority ?? 0)
          )
          .map((column) => {
            return (
              <DataTableToolbarFilter
                key={column.id}
                column={column}
                onDateSelected={onDateSelected}
                itemHeight={columnsAdjustmentHeight}
                selectedDefaultDate={selectedDefaultDate}
              />
            )
          })}
        {isFiltered && (
          <Button
            aria-label="Reset filters"
            variant="outline"
            size="sm"
            onClick={onReset}>
            <Close sx={{ width: 18, height: 18 }} />
            Supprimer les filtres
          </Button>
        )}
        <div className="w--fit">{children}</div>
      </div>
      <div className="flex items-center gap-2">
        {leftAddon}
        {
          !hideColumnsAdjustment &&
          <DataTableViewOptions
            table={table}
            columnsAdjustmentHeight={columnsAdjustmentHeight}
          />
        }
        {excelExportData && (
          <ExportExcel {...excelExportData} className="text-xs h-8" />
        )}
        {rightAddon}
      </div>
    </div>
  )
}

interface DataTableToolbarFilterProps<TData> {
  column: Column<TData>
  onDateSelected?: () => void
  itemHeight?: number
  selectedDefaultDate?: Date | { from: Date; to: Date } | undefined
}

function DataTableToolbarFilter<TData>({
  column,
  onDateSelected,
  itemHeight,
  selectedDefaultDate,
}: DataTableToolbarFilterProps<TData>) {
  {
    const columnMeta = column.columnDef.meta
    const onFilterRender = React.useCallback(() => {
      if (!columnMeta?.variant) return null

      switch (columnMeta.variant) {
        case "text":
          return (
            <Input
              placeholder={columnMeta.placeholder ?? columnMeta.label}
              value={(column.getFilterValue() as string) ?? ""}
              onChange={(event) => column.setFilterValue(event.target.value)}
              className="h-8 w-40 lg:w-56"
            />
          )

        case "number":
          return (
            <div className="relative">
              <Input
                type="number"
                inputMode="numeric"
                placeholder={columnMeta.placeholder ?? columnMeta.label}
                value={(column.getFilterValue() as string) ?? ""}
                onChange={(event) => column.setFilterValue(event.target.value)}
                className={cn("h-8 w-[120px]", columnMeta.unit && "pr-8")}
              />
              {columnMeta.unit && (
                <span className="absolute top-0 right-0 bottom-0 flex items-center rounded-r-md bg-accent px-2 text-muted-foreground text-sm">
                  {columnMeta.unit}
                </span>
              )}
            </div>
          )

        case "date":
        case "dateRange":
        case "monthYearRange":
          return (
            <DataTableDateFilter
              column={column}
              title={columnMeta.label ?? column.id}
              multiple={["dateRange", "monthYearRange"].includes(
                columnMeta.variant
              )}
              onDateSelected={onDateSelected}
              selectedDefaultDate={selectedDefaultDate}
            />
          )

        case "select":
        case "multiSelect":
          return (
            <DataTableFacetedFilter
              column={column}
              title={columnMeta.label ?? column.id}
              options={columnMeta.options ?? []}
              multiple={columnMeta.variant === "multiSelect"}
              itemHeight={itemHeight}
            />
          )

        default:
          return null
      }
    }, [column, columnMeta])

    return onFilterRender()
  }
}
